// Date and number display. Locale-pinned so every screen agrees.

// Render a date for display. Always absolute, with one exception: dates that
// fall on today / yesterday / tomorrow are spelled out in words so recent
// activity reads naturally. Anything else: "8 maj" (current year) or
// "8 maj 2026" (other year). Set `time: true` to append HH:MM — useful for
// timeline events and last-activity chips where the time of day matters.
// Intl formatters are expensive to construct and cheap to reuse, and passing a
// fresh options object to toLocaleDateString defeats V8's internal cache — it was
// measured at roughly 50µs a call against 0.8µs for a reused formatter. Every
// date in the product goes through here, and the table calls it several times a
// row, so the difference was ~160ms on one table render.

const LOCALE = 'sv-SE'

const DATE_SAME_YEAR = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short' })
const DATE_WITH_YEAR = new Intl.DateTimeFormat(LOCALE, {
  day: 'numeric', month: 'short', year: 'numeric',
})
const DATE_WEEKDAY = new Intl.DateTimeFormat(LOCALE, {
  weekday: 'short', day: 'numeric', month: 'short',
})
const DATE_WITH_YEAR_STOCKHOLM = new Intl.DateTimeFormat(LOCALE, {
  timeZone: 'Europe/Stockholm', day: 'numeric', month: 'short', year: 'numeric',
})
const TIME_HM = new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit' })

export const fmtDate = (iso?: string | null, opts: { time?: boolean } = {}): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'

  const now = new Date()
  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const dayDiff = Math.round((startOfDay(d) - startOfDay(now)) / 86400000)

  const timePart = opts.time ? ` ${TIME_HM.format(d)}` : ''

  if (dayDiff === 0) return `Idag${timePart}`
  if (dayDiff === -1) return `Igår${timePart}`
  if (dayDiff === 1) return `Imorgon${timePart}`

  const fmt = d.getFullYear() === now.getFullYear() ? DATE_SAME_YEAR : DATE_WITH_YEAR
  return `${fmt.format(d)}${timePart}`
}

export const fmtTime = (iso?: string | null): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return TIME_HM.format(d)
}

// Bare day + month, no today/yesterday wording — for chart axis ticks, where
// every label has to read the same and stay short.
export const fmtDayMonth = (iso: string): string => DATE_SAME_YEAR.format(new Date(iso))

// Weekday + day + month, for chart tooltips where the day of week is the point.
export const fmtWeekdayDate = (iso: string): string => DATE_WEEKDAY.format(new Date(iso))

// Absolute date pinned to Stockholm, matching the day boundaries the server
// computes its local hours in.
export const fmtDateStockholm = (iso: string): string =>
  DATE_WITH_YEAR_STOCKHOLM.format(new Date(iso))

// Thousands grouping, same locale as the dates. Swedish groups with a space,
// so 1 234 rather than 1,234 — mixing the two put both in one admin screen.
const NUM = new Intl.NumberFormat(LOCALE)

export const fmtNum = (v: number): string => NUM.format(v)
